import type { IArticle, IMagazineImpactMetrics, IResearchTopic } from "~/types";

export type MagazineCoverType = "full" | "thumb";

export function getMagazineArticleUrl(
  article: IArticle,
  baseUrl = ""
): string {
  const slug = article.journal?.journalSlug ?? "";
  if (!slug || !article.doi) {
    return "";
  }
  const prefix = baseUrl.replace(/\/$/, "");
  return `${prefix}/journals/${slug}/articles/${article.doi}/full`;
}

export function getMagazineArticleImageUrl(
  article: IArticle
): string | undefined {
  return article.image?.url || article.imageUrl;
}

export function getCoverType(rt: IResearchTopic): MagazineCoverType {
  return rt.coverImageUrl ? "full" : "thumb";
}

export function getCoverImageUrl(rt: IResearchTopic): string | undefined {
  return rt.coverImageUrl ?? rt.imageUrl;
}

export function isEditorial(article: IArticle): boolean {
  return article.articleType?.toLowerCase() === "editorial";
}

export function findEditorial(articles: IArticle[]): IArticle | undefined {
  return articles.find(isEditorial);
}

/** Plain-text title after stripping markup (matches Meta display rules). */
export function normalizeMagazinePlainText(text: string): string {
  return text
    .replace(/[\u00A0\u200B-\u200D\uFEFF]/g, " ")
    .replace(/&nbsp;|&#160;|&#xA0;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getMagazineArticlePlainTitle(article: IArticle): string {
  const raw = article.title;
  if (typeof raw !== "string") {
    return "";
  }
  return normalizeMagazinePlainText(raw.replace(/<[^>]*>/g, " "));
}

export function hasMagazineArticleTitle(article: IArticle): boolean {
  return getMagazineArticlePlainTitle(article).length > 0;
}

/**
 * First editorial only — extra editorials stay in the list, matching feed Content.tsx.
 * Untitled articles are excluded so they never occupy a grid cell as a blank card.
 * Repeated ids are dropped: paged requests can return the same article twice,
 * and the layout keeps one tile per id, so a second copy leaves an empty cell.
 */
export function getMagazineArticlesForGrid(articles: IArticle[]): IArticle[] {
  const editorial = findEditorial(articles);
  const seenIds = new Set<number>();
  return articles.filter((article) => {
    if (
      !hasMagazineArticleTitle(article) ||
      (editorial && article.id === editorial.id) ||
      seenIds.has(article.id)
    ) {
      return false;
    }
    seenIds.add(article.id);
    return true;
  });
}

export type MagazineEditorialLayout = "vanilla" | "cover-pulled";

/**
 * Temporary stand-in for Feed htmlAbstract until article contents are wired.
 * Prefer BodyText when FieldCode is BODY_TEXT_COUNT (same as ArticlePages mapper).
 */
export function getMagazineEditorialAbstract(article: IArticle): string {
  const text =
    article.fieldCode === "BODY_TEXT_COUNT"
      ? article.bodyText
      : article.abstract || "";
  return text?.trim() ?? "";
}

/**
 * Match rt-magazine-next Editorial.tsx: CoverPulled only for portrait images
 * when the content grid is 2+ columns and an abstract exists.
 */
export function getMagazineEditorialLayout(
  article: IArticle,
  gridSize: number
): MagazineEditorialLayout {
  const hasAbstract = Boolean(getMagazineEditorialAbstract(article));
  const orientation = article.image?.orientation?.toUpperCase();
  const isPortrait = orientation === "P" || orientation === "XP";

  if (gridSize <= 1 || !hasAbstract || !isPortrait) {
    return "vanilla";
  }
  return "cover-pulled";
}

export function mapImpactV5ToMagazineMetrics(
  rt: IResearchTopic
): IMagazineImpactMetrics {
  const views = rt.impactV5?.views ?? 0;
  const articleViews = rt.impactV5?.articleViews ?? 0;
  const articleDownloads = rt.impactV5?.downloads ?? 0;

  return {
    researchTopicId: rt.id,
    views,
    articleViews,
    articleDownloads,
    topicViews: Math.max(0, views - articleViews - articleDownloads)
  };
}

export function parseMagazinePageQuery(page: unknown): number {
  const raw = Array.isArray(page) ? page[0] : page;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) {
    return 1;
  }
  return Math.floor(n);
}

/** Shared by route-handler + server-init middleware. */
export function isMagazineRoute(to: {
  name?: unknown;
  path?: string;
}): boolean {
  return (
    to.name?.toString().includes("-magazine") === true ||
    (typeof to.path === "string" && to.path.endsWith("/magazine"))
  );
}

/** Index, articles, authors and impact, plus the legacy overview and slug-less index aliases. */
const REGULAR_ROUTE_NAMES = [
  "research-topics-rtId",
  "research-topics-rtId-rtSlug",
  "research-topics-rtId-rtSlug-overview",
  "research-topics-rtId-rtSlug-articles",
  "research-topics-rtId-rtSlug-authors",
  "research-topics-rtId-rtSlug-impact"
];

/** Non-magazine Research Topic page. Counterpart of `isMagazineRoute`. */
export function isRegularResearchTopicRoute(to: { name?: unknown }): boolean {
  const name = to.name?.toString();
  return name !== undefined && REGULAR_ROUTE_NAMES.includes(name);
}
